import {
  BMButtonIcon,
  BMComponentsType,
  BMElevationType,
  BMPrimitives,
  BMText,
  BMTray,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCrossClose16 from "@traveloka/icon-kit-web/react/IcSystemCrossClose16";
import { useMemo } from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";

import { MULTI_LOGIN_LOGOGRAM_BASE64 } from "../../../assets/multi-login-logogram";
import { MULTI_LOGIN_LOGOTYPE_BASE64 } from "../../../assets/multi-login-logotype";
import { MULTI_LOGIN_VECTOR_BASE64 } from "../../../assets/multi-login-vector";
import { appendTestId } from "../../../utils";
import type { BMCorporateSelectionPopupProps } from "../types";
import CorporateOptionCard from "./CorporateOptionCard";
import CorporateOptionCardSkeleton from "./CorporateOptionCardSkeleton";

const { BMSize } = BMPrimitives;

const PLACEHOLDER_COUNT = 5;

const BACKGROUND_VECTOR_LEFT = "-30%";
const BACKGROUND_VECTOR_TOP = "-5%";
const BACKGROUND_VECTOR_HEIGHT = "110%";

export default function BMCorporateSelectionMobile({
  isVisible,
  userEmail,
  corporates,
  selectedId,
  onSelect,
  onContinue,
  onClose,
  loading,
  welcomeTemplate,
  headingTemplate,
  continueLabel,
  activeBadgeLabel,
  testID,
}: Omit<BMCorporateSelectionPopupProps, "variant" | "style">) {
  const { BMComponents, BMElevation } = useBMTheme();
  const styles = useMemo(
    () => createStyles(BMComponents, BMElevation),
    [BMComponents, BMElevation]
  );
  const selected = corporates.find((c) => c.id === selectedId);
  const heading = headingTemplate(corporates.length);
  const continueDisabled = !selected || !!loading;

  return (
    <BMTray
      isVisible={isVisible}
      onTrayClose={onClose}
      fitContent
      contentPadding={BMSize.xl}
      headerProps={{ containerStyle: styles.header }}
      testID={appendTestId(testID, "selection-mobile")}
    >
      <View style={styles.body}>
        <View style={styles.backgroundVector} pointerEvents="none">
          <Image
            source={{ uri: MULTI_LOGIN_VECTOR_BASE64 }}
            style={styles.backgroundVectorImage}
            resizeMode="contain"
          />
        </View>
        {onClose && (
          <View style={styles.closeButton}>
            <BMButtonIcon
              Icon={IcSystemCrossClose16}
              size="NORMAL"
              onPress={onClose}
              testID={appendTestId(testID, "close-button")}
            />
          </View>
        )}
        <View style={styles.logo} accessibilityLabel="Traveloka for Corporates">
          <Image
            source={{ uri: MULTI_LOGIN_LOGOGRAM_BASE64 }}
            style={styles.logogram}
            resizeMode="contain"
          />
          <Image
            source={{ uri: MULTI_LOGIN_LOGOTYPE_BASE64 }}
            style={styles.logotype}
            resizeMode="contain"
          />
        </View>
        <BMText variant="uiNormalLi">{welcomeTemplate(userEmail)}</BMText>
        <BMText variant="titleNormal" style={styles.heading}>
          {heading}
        </BMText>
        <View style={styles.cardGroup}>
          {loading
            ? Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
                <CorporateOptionCardSkeleton key={`skeleton-${i}`} variant="mobile" />
              ))
            : corporates.map((corporate) => (
                <CorporateOptionCard
                  key={corporate.id}
                  corporate={corporate}
                  variant="mobile"
                  activeBadgeLabel={activeBadgeLabel}
                  selected={selected?.id === corporate.id}
                  onPress={() => onSelect(corporate)}
                  testID={testID}
                />
              ))}
        </View>
        <View style={styles.footerBar}>
          <Pressable
            onPress={() => selected && onContinue(selected)}
            disabled={continueDisabled}
            testID={appendTestId(testID, "continue-button")}
            accessibilityRole="button"
            style={[
              styles.continueButton,
              continueDisabled && styles.continueButtonDisabled,
            ]}
          >
            <BMText
              variant="uiHuge"
              ink={
                continueDisabled ? "BMUiButtonInkDisabled" : "BMUiButtonInkPrimary"
              }
            >
              {continueLabel}
            </BMText>
          </Pressable>
        </View>
      </View>
    </BMTray>
  );
}

const createStyles = (
  BMComponents: BMComponentsType,
  BMElevation: BMElevationType
) =>
  StyleSheet.create({
    body: {
      gap: BMSize.lg,
      alignItems: "stretch",
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorList,
    },
    header: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorList,
      marginBottom: -BMSize.sm,
    },
    backgroundVector: {
      position: "absolute",
      top: -BMSize.xl,
      bottom: -BMSize.xl,
      left: -BMSize.xl,
      right: -BMSize.xl,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorList,
      overflow: "hidden",
    },
    backgroundVectorImage: {
      position: "absolute",
      left: BACKGROUND_VECTOR_LEFT,
      top: BACKGROUND_VECTOR_TOP,
      height: BACKGROUND_VECTOR_HEIGHT,
      aspectRatio: 562.917 / 708.583,
    },
    closeButton: { position: "absolute", top: 0, right: 0, zIndex: 10 },
    logo: { alignItems: "flex-start", height: 64 },
    logogram: { width: 52, height: 41 },
    logotype: { width: 80, height: 17 },
    heading: { textAlign: "left" },
    cardGroup: {
      gap: BMSize.lg,
      marginTop: BMSize.sm,
      alignItems: "center",
      alignSelf: "stretch",
    },
    footerBar: {
      marginHorizontal: -BMSize.xl,
      marginBottom: -BMSize.xl,
      paddingVertical: BMSize.lg,
      paddingHorizontal: BMSize.xl,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      borderTopWidth: BMPrimitives.BMBorder.width.thick,
      borderTopColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderBottomLeftRadius: BMPrimitives.BMRadius.lg,
      borderBottomRightRadius: BMPrimitives.BMRadius.lg,
      ...BMElevation.elevation2,
    },
    continueButton: {
      height: 48,
      borderRadius: BMPrimitives.BMRadius.rounded,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: BMComponents.BMUiButtonBgcolorPrimary,
      paddingHorizontal: BMSize.lg,
    },
    continueButtonDisabled: {
      backgroundColor: BMComponents.BMUiButtonBgcolorDisabled,
    },
  });
